import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import './Accueil.css';
import '../../composants/ModalArticles/ModalArticles.css';
import Button from '../../composants/Button/Button';
import { useAuth } from '../../contexts/AuthContext';
import AccueilModal from '../../composants/AccueilModal/AccueilModal';
import Annuaire from '../Annuaire/Annuaire';
import Publications from '../Publications/Publications';
import PageStatique from '../../composants/PageStatique/PageStatique';
import SearchBar from '../../composants/SearchBar/SearchBar';
import { LotCard } from '../../composants/LotCard/LotCard';
import fonctionnementAcheteurImg from '../../assets/accueil/fonctionnement-acheteur.webp';
import fonctionnementPrestaireImg from '../../assets/accueil/fonctionnement-prestataire.webp';
import quiSommesNousImg from '../../assets/accueil/qui-sommes-nous.webp';
import logoblanc from '../../assets/logo/logo-centralis-blanc.svg';
import iconClock from '../../assets/accueil/icons/clock.svg';
import iconDecrease from '../../assets/accueil/icons/decrease.svg';
import iconLibrary from '../../assets/accueil/icons/library.svg';
import iconLocation from '../../assets/accueil/icons/location.svg';
import iconThumbUp from '../../assets/accueil/icons/thumb-up.svg';
import iconSafety from '../../assets/accueil/icons/safety.svg';
const heroImg = process.env.PUBLIC_URL + '/accueil-hero.webp';


interface ArticlePublic {
    id: number;
    code: string;
    nom: string;
}

interface LotTechnique {
    id: number;
    code: string;
    nom: string;
}

interface LotTechniqueExt extends LotTechnique {
    /** Image locale (import) ou URL — prioritaire sur getLotImage() */
    customImage?: string;
    /** Catalogue statique quand le lot n'existe pas en BDD */
    staticArticles?: ArticlePublic[];
}

// Nombre de cartes skeleton affichees pendant le chargement des lots.
const LOTS_SKELETON_COUNT = 25;

const getAcheteurStaticLogo = (fileKey: string): string | null => {
    try {
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        return require(`../../assets/accueil/logos-acheteurs/${fileKey}.webp`);
    } catch {
        return null;
    }
};

const getPrestatairStaticLogo = (id: number): string | null => {
    try {
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        return require(`../../assets/accueil/logos-prestataires/${id}.webp`);
    } catch {
        return null;
    }
};

// Fade-in des logos au chargement reel de l'image (pas au montage).
const onLogoLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    e.currentTarget.classList.add('is-loaded');
};
// Garde : si l'image est deja en cache (complete au montage), on l'affiche
// directement, sinon elle resterait invisible (onLoad ne se redeclenche pas).
const markLogoIfCached = (el: HTMLImageElement | null) => {
    if (el && el.complete && el.naturalWidth > 0) {
        el.classList.add('is-loaded');
    }
};

const ACHETEUR_LOGOS: Array<{ name: string; id: number | null; ext?: string; staticKey?: string }> = [
    { name: 'Brigade de sapeurs-pompiers de Paris', id: 370 },
    { name: 'Autorité des marchés financiers', id: null, staticKey: 'amf' },
    { name: 'CNRS', id: 179 },
    { name: 'Commune de Barentin', id: 125, ext: 'png' },
    { name: 'Région Île-de-France', id: 392 },
    { name: 'CRPCEN', id: 119 },
    { name: 'Département de la Seine-Maritime', id: 266 },
    { name: 'Préfecture de Police de Paris', id: 595 },
    { name: 'Université de Pau', id: 54 },
    { name: 'Eurométropole de Metz', id: 273 },
    { name: 'Gendarmerie Nationale', id: 72 },
    { name: 'Métropole Grand Annecy', id: 559, ext: 'png' },
    { name: 'Grand Port Maritime de Marseille', id: 375 },
    { name: 'INRAE', id: 191, ext: 'png' },
    { name: "Ministère de l'Intérieur", id: 162, ext: 'png' },
    { name: 'Ministère Europe et Affaires Étrangères', id: 35 },
    { name: 'Opéra de Paris', id: 491 },
    { name: 'OPAC du Rhône', id: 124 },
    { name: 'Université de Lille', id: 209 },
    { name: 'Ville de Toulon', id: 210 },
    { name: 'Voies Navigables de France', id: 405 },
    { name: 'Centre Hospitalier Aunay Bayeux', id: 530 },
];

const PRESTATAIRE_LOGOS: Array<{ name: string; id: number }> = [
    { name: 'SNEF', id: 14 },
    { name: 'ID VERDE', id: 19 },
    { name: 'ENERLIS', id: 11 },
    { name: 'EIFFAGE', id: 45 },
    { name: 'COLAS', id: 29 },
    { name: 'SOCOTEC', id: 12 },
    { name: 'VERITAS', id: 22 },
    { name: 'DEKRA', id: 13 },
    { name: 'GEOFIT', id: 21 },
    { name: 'SLAP', id: 28 },
    { name: 'HYDROGEOTECHNIQUE', id: 5 },
    { name: 'ESPELIA', id: 2 },
    { name: 'ALCENA', id: 24 },
    { name: 'ENVISOL', id: 7 },
    { name: 'ALPELEC', id: 23 },
];

// LotCard + getLotFamille + LOT_LABELS extraits vers `composants/LotCard/LotCard.tsx` (partages avec
// la page Catalogue acheteur). Le rendu de la mosaique ci-dessous est inchange.

const Accueil = () => {
    const navigate = useNavigate();
    const { user } = useAuth();

    const [stickyNav, setStickyNav] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    useEffect(() => {
        const onScroll = () => setStickyNav(window.scrollY > 80);
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);
    const scrollToSection = (id: string) => {
        const el = document.getElementById(id);
        if (el) {
            const top = el.getBoundingClientRect().top + window.scrollY - 80;
            window.scrollTo({ top, behavior: 'smooth' });
        }
    };

    useEffect(() => {
        if (!user) return;
        const userDataString = localStorage.getItem('user');
        if (userDataString) {
            try {
                const userData = JSON.parse(userDataString);
                const isAcheteur = ['Acheteur', 'Acheteur signataire', 'Acheteur signataire admin'].includes(userData.role);
                navigate(isAcheteur ? '/operations' : '/commandes', { replace: true });
            } catch {
                navigate('/commandes', { replace: true });
            }
        }
    }, [user, navigate]);

    type ModalType = 'acheteurs' | 'catalogue-formalise' | 'catalogue-libre' | 'prestataires' | 'lot-catalogue' | 'publications' | 'video' | 'page-content' | null;
    const [activeModal, setActiveModal] = useState<ModalType>(null);
    const [footerPageNode, setFooterPageNode] = useState<React.ReactNode>(null);
    const [footerPageTitle, setFooterPageTitle] = useState<string>('');

    const openFooterPage = (node: React.ReactNode, title: string) => {
        setFooterPageNode(node);
        setFooterPageTitle(title);
        setActiveModal('page-content');
    };

    const [lots, setLots] = useState<LotTechnique[]>([]);
    const [lotsLoaded, setLotsLoaded] = useState(false);
    const [acheteurCount, setAcheteurCount] = useState<number | null>(null);
    const [prestatairCount, setPrestatairCount] = useState<number | null>(null);
    const [selectedLot, setSelectedLot] = useState<LotTechniqueExt | null>(null);
    const [showArticleLoginPrompt, setShowArticleLoginPrompt] = useState(false);

    const allLots = useMemo(() => {
        return [...lots].sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }));
    }, [lots]);
    const [lotArticles, setLotArticles] = useState<ArticlePublic[]>([]);
    const [loadingArticles, setLoadingArticles] = useState(false);
    const [articlesError, setArticlesError] = useState<string | null>(null);
    const [articlesSearch, setArticlesSearch] = useState('');
    const [fonctionnementExpanded, setFonctionnementExpanded] = useState(false);
    const [fonctionnementPrestaireExpanded, setFonctionnementPrestaireExpanded] = useState(false);

    const filteredArticles = useMemo(() => {
        if (!articlesSearch.trim()) return lotArticles;
        const normalize = (s: string) => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        const terms = normalize(articlesSearch).split(' ').filter(Boolean);
        return lotArticles.filter(a =>
            terms.every(t => normalize(`${a.code} ${a.nom}`).includes(t))
        );
    }, [lotArticles, articlesSearch]);

    useEffect(() => {
        const fetchLots = async () => {
            try {
                const res = await fetch(`${process.env.REACT_APP_API_URL}/lots-technique`);
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const json = await res.json();
                setLots(json.data ?? []);
            } catch {
                // Silencieux — la mosaïque reste vide
            }
        };
        fetchLots().finally(() => setLotsLoaded(true));
    }, []);

    useEffect(() => {
        const fetchAcheteurCount = async () => {
            try {
                const res = await fetch(`${process.env.REACT_APP_API_URL}/acheteur/count`);
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const json = await res.json();
                setAcheteurCount(typeof json.data === 'number' ? json.data : null);
            } catch {
                // Silencieux
            }
        };
        fetchAcheteurCount();
    }, []);

    useEffect(() => {
        const fetchPrestatairCount = async () => {
            try {
                const res = await fetch(`${process.env.REACT_APP_API_URL}/prestataire/count`);
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const json = await res.json();
                setPrestatairCount(typeof json.data === 'number' ? json.data : null);
            } catch {
                // Silencieux
            }
        };
        fetchPrestatairCount();
    }, []);

    const openLotCatalogue = async (lot: LotTechniqueExt) => {
        setSelectedLot(lot);
        setLotArticles([]);
        setArticlesSearch('');
        setArticlesError(null);
        setShowArticleLoginPrompt(false);
        setActiveModal('lot-catalogue');

        // Catalogue statique (lot hardcodé sans API)
        if (lot.staticArticles) {
            const sorted = [...lot.staticArticles].sort((a: ArticlePublic, b: ArticlePublic) =>
                a.code.localeCompare(b.code, undefined, { numeric: true, sensitivity: 'base' })
            );
            setLotArticles(sorted);
            return;
        }

        setLoadingArticles(true);
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/lots-technique/${lot.id}/catalogue-public`);
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const json = await res.json();
            const articles: ArticlePublic[] = (json.data ?? []).slice().sort((a: ArticlePublic, b: ArticlePublic) =>
                a.code.localeCompare(b.code, undefined, { numeric: true, sensitivity: 'base' })
            );
            setLotArticles(articles);
        } catch {
            setArticlesError('Impossible de charger le catalogue pour ce lot.');
        } finally {
            setLoadingArticles(false);
        }
    };

    return (
        <div className="accueil-wrapper">
            {stickyNav && (
                <nav className="accueil-sticky-nav">
                    <img src={logoblanc} alt="Centralis" className="accueil-sticky-nav-logo" />
                    <div className="accueil-sticky-nav-links">
                        <button className="accueil-sticky-nav-link" onClick={() => scrollToSection('section-catalogue')}>Catalogue</button>
                        <button className="accueil-sticky-nav-link" onClick={() => scrollToSection('section-acheteurs')}>Acheteurs</button>
                        <button className="accueil-sticky-nav-link" onClick={() => scrollToSection('section-prestataires')}>Prestataires</button>
                        <button className="accueil-sticky-nav-link" onClick={() => scrollToSection('section-temoignages')}>Témoignages</button>
                        <button className="accueil-sticky-nav-link" onClick={() => scrollToSection('section-qui-sommes-nous')}>Qui sommes-nous</button>
                        <button className="accueil-sticky-nav-link" onClick={() => scrollToSection('section-medias')}>Médias</button>
                    </div>
                    <div className="accueil-sticky-nav-auth">
                        <button className="accueil-sticky-nav-link accueil-sticky-nav-link--auth" onClick={() => navigate('/login')}>Connexion</button>
                        <span className="accueil-sticky-nav-separator">/</span>
                        <button className="accueil-sticky-nav-link accueil-sticky-nav-link--auth" onClick={() => navigate('/register')}>Inscription</button>
                    </div>
                    <button
                        className={`accueil-sticky-nav-hamburger${mobileMenuOpen ? ' accueil-sticky-nav-hamburger--open' : ''}`}
                        onClick={() => setMobileMenuOpen(v => !v)}
                        aria-label={mobileMenuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
                        aria-expanded={mobileMenuOpen}
                    >
                        <span></span>
                        <span></span>
                        <span></span>
                    </button>
                    {mobileMenuOpen && (
                        <div className="accueil-sticky-nav-mobile-menu">
                            <button className="accueil-sticky-nav-link" onClick={() => { scrollToSection('section-catalogue'); setMobileMenuOpen(false); }}>Catalogue</button>
                            <button className="accueil-sticky-nav-link" onClick={() => { scrollToSection('section-acheteurs'); setMobileMenuOpen(false); }}>Acheteurs</button>
                            <button className="accueil-sticky-nav-link" onClick={() => { scrollToSection('section-prestataires'); setMobileMenuOpen(false); }}>Prestataires</button>
                            <button className="accueil-sticky-nav-link" onClick={() => { scrollToSection('section-temoignages'); setMobileMenuOpen(false); }}>Témoignages</button>
                            <button className="accueil-sticky-nav-link" onClick={() => { scrollToSection('section-qui-sommes-nous'); setMobileMenuOpen(false); }}>Qui sommes-nous</button>
                            <button className="accueil-sticky-nav-link" onClick={() => { scrollToSection('section-medias'); setMobileMenuOpen(false); }}>Médias</button>
                            <div className="accueil-sticky-nav-mobile-auth">
                                <button className="accueil-sticky-nav-link accueil-sticky-nav-link--auth" onClick={() => navigate('/login')}>Connexion</button>
                                <span className="accueil-sticky-nav-separator">/</span>
                                <button className="accueil-sticky-nav-link accueil-sticky-nav-link--auth" onClick={() => navigate('/register')}>Inscription</button>
                            </div>
                        </div>
                    )}
                </nav>
            )}
            {/* <div className="iframe-wrapper">
                <iframe
                    className="iframe-landing"
                    src="https://centralis.site/?site=centraliseuhideicon"
                    title="Centralis"
                />
            </div> */}
            <section
                className="accueil-hero-section"
                style={{ backgroundImage: `url(${heroImg})` }}
            >
                <nav className="accueil-hero-nav">
                    <span className="accueil-hero-nav-link" onClick={() => navigate('/login')}>Connexion</span>
                    <span className="accueil-hero-nav-link" onClick={() => navigate('/register')}>Inscription gratuite</span>
                </nav>
                <div className="accueil-hero-bottom">
                    <img src={logoblanc} alt="Centralis" className="accueil-hero-logo" />
                    <h1 className="accueil-hero-tagline">Centrale nationale d'achats publics dédiée à la rénovation de patrimoine</h1>
                </div>
            </section>
            <section className="accueil-avantages-banner">
                <div className="accueil-avantage-banner-item">
                    <div className="accueil-avantage-banner-icon-wrap"><img src={iconClock} alt="Gain de temps" className="accueil-avantage-banner-icon" /></div>
                    <h4>GAIN DE TEMPS</h4>
                    <p>Dispensez-vous des procédures de passation, elles sont déjà réalisées !</p>
                </div>
                <div className="accueil-avantage-banner-item">
                    <div className="accueil-avantage-banner-icon-wrap"><img src={iconDecrease} alt="Réduction des coûts" className="accueil-avantage-banner-icon" /></div>
                    <h4>RÉDUCTION DES COÛTS</h4>
                    <p>Profitez de la mutualisation des achats afin de diminuer vos dépenses.</p>
                </div>
                <div className="accueil-avantage-banner-item">
                    <div className="accueil-avantage-banner-icon-wrap"><img src={iconLibrary} alt="Catalogue spécialisé" className="accueil-avantage-banner-icon accueil-avantage-banner-icon--small" /></div>
                    <h4>CATALOGUE SPÉCIALISÉ</h4>
                    <p>Sélectionnez parmi un catalogue conséquent de plus de 290 000 prestations.</p>
                </div>
                <div className="accueil-avantage-banner-item">
                    <div className="accueil-avantage-banner-icon-wrap"><img src={iconLocation} alt="Proximité" className="accueil-avantage-banner-icon" /></div>
                    <h4>PROXIMITÉ</h4>
                    <p>Les prestataires sont souvent situés à proximité des opérations en métropole et DOM.</p>
                </div>
                <div className="accueil-avantage-banner-item">
                    <div className="accueil-avantage-banner-icon-wrap"><img src={iconThumbUp} alt="Achats responsables" className="accueil-avantage-banner-icon" /></div>
                    <h4>ACHATS RESPONSABLES</h4>
                    <p>Tous les prestataires ont des engagements environnementaux et sociaux à respecter.</p>
                </div>
                <div className="accueil-avantage-banner-item">
                    <div className="accueil-avantage-banner-icon-wrap"><img src={iconSafety} alt="Sécurité" className="accueil-avantage-banner-icon" /></div>
                    <h4>SÉCURITÉ</h4>
                    <p>Démarche de sécurité de l'information alignée sur l'ISO/IEC 27001.</p>
                </div>
            </section>
            {allLots.length > 0 && (
                <section id="section-catalogue" className="accueil-lots-mosaic-section">
                    <div className="accueil-lots-mosaic-header">
                        <h2>Nos lots techniques</h2>
                        <p>Cliquez sur un lot pour explorer son catalogue de prestations</p>
                    </div>
                    <div className="accueil-lots-mosaic">
                        {lotsLoaded
                            ? allLots.map((lot) => (
                                <LotCard key={lot.id} lot={lot} onClick={() => openLotCatalogue(lot)} />
                            ))
                            : Array.from({ length: LOTS_SKELETON_COUNT }).map((_, i) => (
                                <div key={`lot-skeleton-${i}`} className="accueil-lot-card accueil-lot-card--skeleton" aria-hidden="true">
                                    <div className="accueil-lot-card-img-wrap accueil-skeleton-shimmer" />
                                    <div className="accueil-lot-card-body">
                                        <span className="accueil-skeleton-line accueil-skeleton-line--code" />
                                        <span className="accueil-skeleton-line accueil-skeleton-line--nom" />
                                    </div>
                                </div>
                            ))}
                    </div>
                </section>
            )}
            <section id="section-acheteurs" className="accueil-acheteurs-banner">
                <div className="accueil-acheteurs-logos">
                    <div className="accueil-logos-track">
                        {[...ACHETEUR_LOGOS, ...ACHETEUR_LOGOS].map(({ name, id, staticKey }, i) => {
                            const fileKey = staticKey ?? (id !== null ? String(id) : null);
                            const staticSrc = fileKey ? getAcheteurStaticLogo(fileKey) : null;
                            const handleError = (e: React.SyntheticEvent<HTMLImageElement>) => {
                                const img = e.currentTarget;
                                const fb = img.getAttribute('data-fallback');
                                if (fb && !img.getAttribute('data-fallback-tried')) {
                                    img.setAttribute('data-fallback-tried', '1');
                                    img.src = fb;
                                } else {
                                    img.style.display = 'none';
                                    const textFb = img.nextElementSibling as HTMLElement;
                                    if (textFb) textFb.style.display = 'block';
                                }
                            };
                            return (
                                <div key={`${name}-${i}`} className="accueil-acheteur-logo-placeholder">
                                    {staticSrc ? (
                                        <>
                                            <img
                                                src={staticSrc}
                                                data-fallback={id !== null ? `${process.env.REACT_APP_API_URL}/annuaire/acheteurs/${id}/logo` : ''}
                                                alt={name}
                                                className="accueil-prestataire-logo-img"
                                                ref={markLogoIfCached}
                                                onLoad={onLogoLoad}
                                                onError={handleError}
                                            />
                                            <span className="accueil-prestataire-logo-fallback">{name}</span>
                                        </>
                                    ) : id !== null ? (
                                        <>
                                            <img
                                                src={`${process.env.REACT_APP_API_URL}/annuaire/acheteurs/${id}/logo`}
                                                alt={name}
                                                className="accueil-prestataire-logo-img"
                                                ref={markLogoIfCached}
                                                onLoad={onLogoLoad}
                                                onError={(e) => {
                                                    e.currentTarget.style.display = 'none';
                                                    const textFb = e.currentTarget.nextElementSibling as HTMLElement;
                                                    if (textFb) textFb.style.display = 'block';
                                                }}
                                            />
                                            <span className="accueil-prestataire-logo-fallback">{name}</span>
                                        </>
                                    ) : (
                                        <span className="accueil-prestataire-logo-fallback" style={{ display: 'block' }}>{name}</span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
                <div className="accueil-acheteurs-info">
                    <span className="accueil-acheteurs-number">
                        {acheteurCount !== null ? `+ ${acheteurCount}` : '—'}
                    </span>
                    <span className="accueil-link accueil-acheteurs-link" onClick={() => setActiveModal('acheteurs')}>
                        Voir tous nos acheteurs →
                    </span>
                </div>
            </section>
            <section className="accueil-fonctact-section">
                <div className="accueil-fonctact-text">
                    <h2 className="accueil-fonctact-title">Le fonctionnement pour les acheteurs</h2>
                    <p>
                        La Centrale est exclusivement réservée aux acheteurs publics, tels que l’État, les Collectivités,
                        les Intercommunalités, les Régions, les Départements, les hôpitaux, les universités, les bailleurs
                        sociaux et les établissements publics. Centralis opère en qualité de mandataire, représentant
                        l’ensemble de ses acheteurs, afin de procéder à la passation d’accords-cadres nationaux
                        conformément à des procédures formalisées supérieures aux seuils européens.
                        Conformément à l’article L2113-4 du Code de la commande publique,
                        « les acheteurs qui recourent à une centrale d’achat pour la réalisation de travaux
                        ou l’acquisition de services sont considérés comme ayant respecté leurs obligations
                        de <strong>publicité et de mise en concurrence</strong> ».
                    </p>
                    {!fonctionnementExpanded ? (
                        <button className="accueil-voir-suite" onClick={() => setFonctionnementExpanded(true)}>
                            Voir la suite ↓
                        </button>
                    ) : (
                        <>
                            <button className="accueil-voir-suite accueil-voir-suite--reduire" onClick={() => setFonctionnementExpanded(false)}>
                                Réduire ↑
                            </button>
                            <p>
                                Ainsi, vous avez la possibilité de commander sans délai diverses prestations parmi
                                un catalogue de plus de 1 500 lots déjà notifiés aux titulaires en France
                                (métropole et DOM). Même pour des opérations excédant les seuils des marchés
                                formalisés, vous pouvez directement notifier un Bon de Commande (pour des
                                commandes standards) ou lancer un Marché Subséquent (pour des commandes
                                sur-mesure), ce qui représente un gain de temps considérable.
                            </p>
                            <p>
                                Centralis ne perçoit aucune rémunération de la part des acheteurs.
                                Ce service est donc <strong>gratuit et sans engagement</strong> pour votre organisme.
                            </p>
                            <ol className="accueil-fonctact-steps">
                                <li>
                                    <strong>Sélection :</strong> Au sein du catalogue, vous sélectionnez les
                                    prestations qui vous intéressent et les ajoutez à votre panier (ou vous
                                    sollicitez l’entreprise de rang 1 afin qu’elle vous transmette un panier
                                    entièrement prérempli, pour vérification et validation). Si le catalogue
                                    ne répond pas à vos besoins, vous pouvez initier un marché subséquent
                                    (sur-mesure) et attendre les offres des titulaires.
                                </li>
                                <li>
                                    <strong>Commande :</strong> Le « valideur » du compte procède à la
                                    validation du panier. La centrale transmet le bon de commande à l’entreprise
                                    concernée, qui dispose d’un délai de 10 jours pour accepter ou refuser la
                                    mission. En cas de refus, et avec votre accord, la commande est transmise
                                    à l’entreprise de rang suivant.
                                </li>
                                <li>
                                    <strong>Paiement :</strong> L’entreprise, dans le cadre du contrat,
                                    facture ses prestations directement dans votre compte Chorus Pro, sans
                                    transiter par Centralis. Vous conservez un contrôle total sur le paiement
                                    et les éventuelles pénalités. Si l’entreprise souhaite recourir à des
                                    sous-traitants, elle vous soumet un DC4 pour approbation.
                                </li>
                                <li>
                                    <strong>Délais :</strong> Chaque entreprise est tenue de respecter le
                                    délai d’exécution contractuel, éventuellement modifié par dérogation.
                                    Le non-respect de ce délai peut entraîner l’application de pénalités.
                                </li>
                            </ol>
                        </>
                    )}
                    <div className="accueil-fonctact-cta">
                        <Button value="S'inscrire (gratuit et sans engagement)" variant="primary" onClick={() => navigate('/register', { state: { step: 1, isAcheteur: true } })} />
                        <Button value="Voir Conditions Générales" variant="secondary" onClick={() => openFooterPage(<PageStatique slug="conditions-generales" />, 'Conditions Générales')} />
                    </div>
                </div>
                <div className="accueil-fonctact-image">
                    <img src={fonctionnementAcheteurImg} loading="lazy" decoding="async" alt="Fonctionnement pour les acheteurs" />
                </div>
            </section>
            <section id="section-prestataires" className="accueil-acheteurs-banner">
                <div className="accueil-acheteurs-logos">
                    <div className="accueil-logos-track">
                        {[...PRESTATAIRE_LOGOS, ...PRESTATAIRE_LOGOS].map(({ name, id }, i) => {
                            const staticSrc = getPrestatairStaticLogo(id);
                            return (
                                <div key={`${name}-${i}`} className="accueil-acheteur-logo-placeholder">
                                    <img
                                        src={staticSrc ?? `${process.env.REACT_APP_API_URL}/entites/${id}/logo`}
                                        data-fallback={staticSrc ? `${process.env.REACT_APP_API_URL}/entites/${id}/logo` : ''}
                                        alt={name}
                                        className="accueil-prestataire-logo-img"
                                        ref={markLogoIfCached}
                                        onLoad={onLogoLoad}
                                        onError={(e) => {
                                            const img = e.currentTarget;
                                            const fb = img.getAttribute('data-fallback');
                                            if (fb && !img.getAttribute('data-fallback-tried')) {
                                                img.setAttribute('data-fallback-tried', '1');
                                                img.src = fb;
                                            } else {
                                                img.style.display = 'none';
                                                const textFb = img.nextElementSibling as HTMLElement;
                                                if (textFb) textFb.style.display = 'block';
                                            }
                                        }}
                                    />
                                    <span className="accueil-prestataire-logo-fallback">{name}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>
                <div className="accueil-acheteurs-info">
                    <span className="accueil-acheteurs-number">
                        {prestatairCount !== null ? `+ ${prestatairCount}` : '—'}
                    </span>
                    <span className="accueil-link accueil-acheteurs-link" onClick={() => setActiveModal('prestataires')}>
                        Voir tous nos prestataires →
                    </span>
                </div>
            </section>
            <section className="accueil-fonctact-section accueil-fonctact-section--reversed">
                <div className="accueil-fonctact-image">
                    <img src={fonctionnementPrestaireImg} loading="lazy" decoding="async" alt="Fonctionnement pour les prestataires" />
                </div>
                <div className="accueil-fonctact-text">
                    <h2 className="accueil-fonctact-title">Le fonctionnement pour les prestataires</h2>
                    <p>
                        Toute entreprise disposant de la compétence et de la capacité requises pour satisfaire un lot
                        peut s'inscrire à tout moment. Il est impératif de remplir intégralement une fiche d'entreprise
                        afin d'apparaître dans l'annuaire en ligne en tant que « prestataire libre ». Cette inscription
                        permet aux acheteurs de solliciter vos services à tout moment pour des Commandes Directes.
                        De plus, vous recevrez des alertes et pourrez consulter l'onglet « publications » afin de
                        candidater aux appels d'offres de la centrale d'achat, également publiés sur les sites BOAMP et JOUE.
                    </p>
                    {!fonctionnementPrestaireExpanded ? (
                        <button className="accueil-voir-suite" onClick={() => setFonctionnementPrestaireExpanded(true)}>
                            Voir la suite ↓
                        </button>
                    ) : (
                        <>
                            <button className="accueil-voir-suite accueil-voir-suite--reduire" onClick={() => setFonctionnementPrestaireExpanded(false)}>
                                Réduire ↑
                            </button>
                            <p>
                                Le Règlement de Consultation précise les modalités utilisées, notamment les critères
                                de notation. Les appels d'offres sont publiés l'été des années paires pendant une durée
                                de trois mois et notifiés au 1er janvier de l'année impaire suivante. En cas de succès
                                lors d'un appel d'offres, votre statut évoluera de « libre » à « titulaire ». En tant
                                que titulaire d'un ou de plusieurs lots de Centralis, vous pourrez être directement
                                notifié d'un Bon de Commande pour des commandes standards, ou être consulté pour un
                                Marché Subséquent pour des commandes sur-mesure, par tout acheteur public de la centrale.
                                Bien entendu, vous conservez la maîtrise totale du marché tout au long de sa durée,
                                ce qui vous permet d'accepter ou de refuser chaque commande individuellement.
                            </p>
                            <p>
                                Centralis se rémunère en appliquant une <strong>commission de 5%</strong> sur le montant
                                des commandes que vous recevez.
                            </p>
                            <ol className="accueil-fonctact-steps">
                                <li>
                                    <strong>Sélection :</strong> L'acheteur vous contactera généralement préalablement
                                    par téléphone (vos coordonnées figurent dans l'annuaire) afin d'aborder l'opération.
                                    Il élaborera ensuite le panier, soit de manière autonome, soit en sollicitant votre
                                    assistance. Dans ce dernier cas, vous sélectionnerez les prestations requises ainsi
                                    que les métrés correspondants dans le catalogue et les ajouterez au panier. Il vous
                                    incombera ensuite de lui transmettre ce panier, soit via la plateforme, soit en lui
                                    communiquant un numéro dédié.
                                </li>
                                <li>
                                    <strong>Commande :</strong> Une notification vous sera envoyée par courriel et via
                                    la plateforme. Vous disposerez alors de plusieurs jours (un minuteur est affiché à
                                    titre indicatif) pour accepter ou refuser le bon de commande. À l'issue de ce délai,
                                    en l'absence de réponse de votre part, la commande sera transmise à l'entreprise
                                    de rang suivant.
                                </li>
                                <li>
                                    <strong>Paiement :</strong> Vous adresserez vos factures directement à l'acheteur
                                    commanditaire dans son compte Chorus Pro, sans transiter par Centralis. Si vous
                                    envisagez de recourir à des sous-traitants, vous soumettrez le DC4 à l'approbation
                                    de l'acheteur de l'opération concernée.
                                </li>
                                <li>
                                    <strong>Délais :</strong> Chaque entreprise est tenue de respecter le délai
                                    d'exécution contractuel, susceptible d'être modifié par dérogation. Le non-respect
                                    de ce délai peut entraîner l'application de pénalités.
                                </li>
                            </ol>
                        </>
                    )}
                    <div className="accueil-fonctact-cta">
                        <Button value="S'inscrire" variant="primary" onClick={() => navigate('/register', { state: { step: 2, isAcheteur: false } })} />
                        <Button value="Voir les publications" variant="secondary" onClick={() => setActiveModal('publications')} />
                    </div>
                </div>
            </section>

            {/* Témoignages */}
            <section id="section-temoignages" className="accueil-temoignages-section">
                <div className="accueil-temoignages-header">
                    <h2>Témoignages</h2>
                </div>
                <div className="accueil-temoignages-grid-wrapper">
                    <div className="accueil-temoignages-grid">
                        {[
                            {
                                quote: "Nous notons un gain de temps énorme, même si nous ne l'utilisons pour l'instant que pour des opérations \"simples\". Finalement son utilisation rend la sécurisation de l'achat parfaite. Je recommande vivement.",
                                author: "Université de Technologie de Belfort Montbéliard",
                            },
                            {
                                quote: "Les résultats sont à la hauteur de nos attentes : des utilisateurs satisfaits et une collaboration renforcée, que nous souhaitons voir se poursuivre dans la durée. Nous sommes pleinement satisfaits de travailler en partenariat avec Centralis Europe.",
                                author: "Infratec",
                            },
                            {
                                quote: "Un outil parfait pour simplifier la commande publique.",
                                author: "Les Compagnons du Barroux",
                            },
                            {
                                quote: "Efficacité et rapidité pour le client et l'entreprise.",
                                author: "Eiffage Construction",
                            },
                            {
                                quote: "Centralis est une plateforme agile pour les acheteurs publics et les entreprises. Cela permet d'améliorer grandement la rapidité de traitement des marchés. En tant qu'entreprise cela permet de nouer des contacts avec de nouveaux MOA.",
                                author: "Sodeka",
                            },
                        ].map((t, i) => (
                            <div key={i} className="accueil-temoignage-card">
                                <span className="accueil-temoignage-quote-icon">&ldquo;</span>
                                <p className="accueil-temoignage-text">{t.quote}</p>
                                <span className="accueil-temoignage-author">{t.author}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
            <section id="section-qui-sommes-nous" className="accueil-quisommes-section">
                <div className="accueil-quisommes-image">
                    <img src={quiSommesNousImg} loading="lazy" decoding="async" alt="Qui sommes-nous — Centralis" />
                </div>
                <div className="accueil-quisommes-text">
                    <h2 className="accueil-quisommes-title">Qui sommes-nous&nbsp;?</h2>
                    <p>
                        Centralis® est une centrale nationale d&rsquo;achat public réservée aux acheteurs publics,
                        constituée en 2018 sous forme associative, qui est dédiée à la rénovation et l&rsquo;entretien
                        de patrimoine immobilier sur l&rsquo;ensemble du territoire (métropole et DOM). Son champ
                        d&rsquo;action et ses modalités d&rsquo;intervention sont strictement encadrés par les dispositions
                        de l&rsquo;article L. 2113-2 du Code de la commande publique.
                    </p>
                    <p>
                        La stratégie et la gouvernance de Centralis® sont contrôlés par un Conseil de Surveillance
                        exclusivement composé d&rsquo;acheteurs publics. La plateforme www.centralis-europe.com est
                        mise à disposition des utilisateurs afin de centraliser et de sécuriser l&rsquo;ensemble des
                        processus relatifs à l&rsquo;inscription et au référencement des utilisateurs, à la publication
                        des avis, à la gestion des catalogues électroniques, au cryptage des données, à la
                        réception dématérialisée des offres, au stockage et aux mises à jour des pièces de
                        candidature, à l&rsquo;assistance à l&rsquo;analyse par intelligence artificielle, à l&rsquo;échange
                        d&rsquo;informations et de notifications, à la signature électronique, à la traçabilité et à
                        l&rsquo;historisation des actions, ainsi qu&rsquo;à la transparence des marchés lancés par les
                        acheteurs publics.
                    </p>
                    <div className="accueil-quisommes-cta">
                        <Button
                            value="Voir la vidéo de présentation"
                            variant="primary"
                            onClick={() => setActiveModal('video')}
                        />
                    </div>
                </div>
            </section>

            {/* Presse */}
            <section id="section-medias" className="accueil-presse-section">
                <h2 className="accueil-presse-section-title">Centralis dans les medias</h2>
                <div className="accueil-presse-section-list">
                    <div className="accueil-presse-section-card">
                        <div className="accueil-presse-section-logo-wrap">
                            <img src={require('../../assets/about/logo-moniteur.webp')} loading="lazy" decoding="async" alt="Le Moniteur" />
                        </div>
                        <div className="accueil-presse-section-body">
                            <p>&laquo;&nbsp;Le succès de Centralis est au rendez-vous&nbsp;&raquo;</p>
                            <a href="https://www.lemoniteur.fr/article/centralis-la-centrale-d-achats-publics-qui-gagne-du-terrain.2098484" target="_blank" rel="noopener noreferrer">Voir l&rsquo;article</a>
                        </div>
                    </div>
                    <div className="accueil-presse-section-card">
                        <div className="accueil-presse-section-logo-wrap">
                            <img src={require('../../assets/about/logo-wekafr-small.webp')} loading="lazy" decoding="async" alt="Weka" />
                        </div>
                        <div className="accueil-presse-section-body">
                            <p>&laquo;&nbsp;Une solution clé en main pour les acheteurs publics&nbsp;&raquo;</p>
                            <a href="https://www.weka.fr/actualite/commande-publique/article/centralis-la-petite-centrale-d-achats-publics-voit-grand-103971/" target="_blank" rel="noopener noreferrer">Voir l&rsquo;article</a>
                        </div>
                    </div>
                    <div className="accueil-presse-section-card">
                        <div className="accueil-presse-section-logo-wrap">
                            <img src={require('../../assets/about/logo-federation-des-epl.webp')} loading="lazy" decoding="async" alt="Fédération des EPL" />
                        </div>
                        <div className="accueil-presse-section-body">
                            <p>&laquo;&nbsp;Une solution appréciée par plusieurs acheteurs publics dans le secteur de la rénovation de patrimoine immobilier&nbsp;&raquo;</p>
                            <a href="https://www.lesepl.fr/epl-en-action/centralis-la-petite-centrale-dachats-publics-qui-monte-qui-monte/" target="_blank" rel="noopener noreferrer">Voir l&rsquo;article</a>
                        </div>
                    </div>
                </div>
            </section>

            {/* <div className="accueil-page">
                <div className="accueil-header">
                    <h1 className="accueil-title">
                        Centrale Nationale d'Achat Public<br />
                        dédiée à la rénovation de patrimoine
                    </h1>
                    <p className="accueil-subtitle">
                        Vous êtes soumis au Code de la commande publique ?
                    </p>
                    <p className="accueil-subtitle">
                        Vous souhaitez entretenir ou rénover le patrimoine dont vous avez la responsabilité ?
                    </p>
                </div>

                <div className='accueil-section-block'>
                    <div className="accueil-about">
                        <div className="accueil-about-image">
                            <img src={require('../../assets/about/fournisseurs.webp')} loading="lazy" decoding="async" alt="Chantier de rénovation de patrimoine" />
                        </div>
                        <div className="accueil-about-text">
                            <h2 className="accueil-about-title">
                                <span className="accueil-qui">Qui</span> sommes-nous ?
                            </h2>
                            <p>
                                Créée en 2018 sous forme associative, <strong>Centralis®</strong> est une{' '}
                                <strong>centrale d'achat public</strong>{' '}
                                dédiée à la rénovation et à l'entretien de patrimoine immobilier. Son rôle et ses
                                modalités d'intervention sont définis par l'article L. 2113-2 du Code de la commande
                                publique.
                            </p>
                            <p>
                                Plusieurs centaines d'acheteurs répartis sur l'ensemble du territoire utilisent Centralis.
                                Parmi eux, on compte aussi bien des collectivités, des intercommunalités,
                                des établissements et des offices publics, des syndicats, des sociétés publiques
                                locales ou des sociétés d'économie mixte.{' '}
                                <span className="accueil-link" onClick={() => setActiveModal('acheteurs')}>Voir la liste des acheteurs</span>
                            </p>
                            <p>
                                Inscrivez-vous pour accéder au catalogue Centralis et effectuer des achats en ligne, en
                                dispense de procédure, parmi une vingtaine de catégories de métier. L'inscription est
                                gratuite et sans engagement.
                            </p>
                        </div>
                    </div>
                </div>

                <div className="accueil-section-block">
                    <div className="accueil-mutualisation">
                        <div className="accueil-mutualisation-content">
                            <h2 className="accueil-mutualisation-title">
                                La mutualisation des achats
                            </h2>
                            <span className="accueil-mutualisation-quote">
                                « Les acheteurs qui recourent à une centrale d'achat pour la
                                réalisation de travaux ou l'acquisition de services sont
                                considérés comme ayant respecté leurs obligations de
                                publicité et de mise en concurrence ». &nbsp;
                            </span>
                            <span>
                                Article L.2113-4 du
                                Code de la commande publique.
                            </span>
                        </div>
                        <div className="accueil-mutualisation-image">
                            <img src={require('../../assets/about/bureau.webp')} loading="lazy" decoding="async" alt="Mutualisation des achats" />
                        </div>
                    </div>

                    <div className="accueil-avantages">
                        <div className="accueil-avantage">
                            <div className="accueil-avantage-icon">
                                <img src={require('../../assets/about/1-Gain-de-temps.webp')} loading="lazy" decoding="async" alt="Prestataire en intervention" />
                            </div>
                            <h3 className="accueil-avantage-title">GAIN DE TEMPS</h3>
                            <p>Dispensez-vous des procédures de passation, gagner du temps et commandez directement</p>
                        </div>
                        <div className="accueil-avantage">
                            <div className="accueil-avantage-icon">
                                <img src={require('../../assets/about/2-cout-avantageux.webp')} loading="lazy" decoding="async" alt="Prestataire en intervention" />
                            </div>
                            <h3 className="accueil-avantage-title">COÛTS AVANTAGEUX</h3>
                            <p>Bénéficiez de la mutualisation des achats et faites baisser vos coûts</p>
                        </div>
                        <div className="accueil-avantage">
                            <div className="accueil-avantage-icon accueil-avantage-icon-orange">
                                <img src={require('../../assets/about/3-3000prestations.webp')} loading="lazy" decoding="async" alt="Prestataire en intervention" />
                            </div>
                            <h3 className="accueil-avantage-title">290 000 PRESTATIONS</h3>
                            <p>Choisissez dans un catalogue parmi plus de 290 000 prestations</p>
                        </div>
                        <div className="accueil-avantage">
                            <div className="accueil-avantage-icon">
                                <img src={require('../../assets/about/4-proximite.webp')} loading="lazy" decoding="async" alt="Prestataire en intervention" />

                            </div>
                            <h3 className="accueil-avantage-title">PROXIMITÉ</h3>
                            <p>L'ensemble du territoire est couvert par des prestataires souvent situés à proximité des acheteurs</p>
                        </div>
                        <div className="accueil-avantage">
                            <div className="accueil-avantage-icon">
                                <img src={require('../../assets/about/5-achat-responsable.webp')} loading="lazy" decoding="async" alt="Prestataire en intervention" />
                            </div>
                            <h3 className="accueil-avantage-title">ACHATS RESPONSABLES</h3>
                            <p>Seulement les meilleures offres de prestataires qui ont tous des engagements environnementaux à respecter</p>
                        </div>
                    </div>
                </div>



                <div className="accueil-section-block">
                    <div className="accueil-catalogue">
                        <div className="accueil-catalogue-top">
                            <div className="accueil-catalogue-image">
                                <img src={require('../../assets/about/isolation.webp')} loading="lazy" decoding="async" alt="Prestataire en intervention" />
                            </div>
                            <div className="accueil-catalogue-lots">
                                <h2 className="accueil-catalogue-title">
                                    Plusieurs milliers de prestations<br />
                                    <span className='accueil-highlight'>disponibles immédiatement</span>
                                </h2>
                                <div className="accueil-lots-grid">
                                    <div className="accueil-lots-col">
                                        <p>101 - Assistance à maitrise d'ouvrage</p>
                                        <p>102 - Maîtrise d'œuvre Bâtiment</p>
                                        <p>103 - Urbaniste - Paysagiste</p>
                                        <p>104 - Bureau d'Étude Voirie et Réseaux Divers</p>
                                        <p>105 - Bureau de contrôle</p>
                                        <p>106 - Coordonnateur Sécurité et Protection Santé</p>
                                        <p>107 - Ordonnancement Pilotage et Coordination</p>
                                        <p>108 - Géomètre</p>
                                        <p>109 - Bureau d'Étude Géotechnique</p>
                                        <p>110 - Bureau d'Étude Environnement</p>
                                        <p>111 - Bureau d'Étude Pollution</p>
                                        <p>112 - Diagnostiqueur Technique, Énergie, Amiante et Plomb</p>
                                        <p>113 - Numérisation du patrimoine - BIM</p>
                                    </div>
                                    <div className="accueil-lots-col">
                                        <p>201 - Démolition - Désamiantage</p>
                                        <p>202 - Gros œuvre, Maçonnerie et Façade</p>
                                        <p>203 - Charpente - Étanchéité - Couverture - Bardage</p>
                                        <p>204 - Cloison - Faux-plafonds - Plâtrerie – Isolation</p>
                                        <p>205 - Menuiserie et Serrurerie</p>
                                        <p>206 - Courant fort - Courant faible - Domotique</p>
                                        <p>207 - Chauffage - Ventilation - Plomberie</p>
                                        <p>208 - Peinture et revêtements</p>
                                        <p>209 - Espaces verts</p>
                                        <p>210 - Voirie et réseaux divers</p>
                                        <p>211 - Nettoyage</p>
                                        <p>212 - Multiservices et performances énergétiques</p>
                                    </div>
                                </div>
                                <div className="accueil-catalogues-desc">
                                    <p>
                                        <strong>Le catalogue issu de procédures formalisées :</strong> Dans le cadre précis de nos accords-cadres multi-attributaires, à
                                        bons de commande et à marchés subséquents notifiés aux titulaires, vous pouvez commander sans délai
                                        diverses prestations en dispense totale de procédure. <span className="accueil-link" onClick={() => setActiveModal('catalogue-formalise')}>Voir le catalogue formalisé</span>
                                    </p>
                                    <p>
                                        <strong>Le catalogue issu d'un référencement libre :</strong> Dans cet espace créé par les entreprises qui proposent librement
                                        leurs prestations, il appartient à chaque acheteur de vérifier le profil de l'entreprise et de respecter les seuils
                                        d’achat ainsi que les règles de passation imposées par le Code de la commande publique. <span className="accueil-link" onClick={() => setActiveModal('catalogue-libre')}>Voir le catalogue libre</span>
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>



                <div className="accueil-section-block">
                    <div className="accueil-entreprises">
                        <div className="accueil-entreprises-content">
                            <h2 className="accueil-entreprises-title">
                                Des entreprises performantes sur tout le territoire
                            </h2>
                            <p>
                                Toute entreprise liée à l'objet de Centralis a la possibilité de se faire référencer pour
                                proposer ses prestations dans notre « catalogue libre » à tout moment.
                            </p>
                            <p>
                                En ce qui concerne le « catalogue formalisé », afin de permettre à un maximum
                                d'entreprises d'accéder à nos procédures formalisées, nos accords-cadres sont allotis
                                par métier et par secteur géographique (échelle départementale pour les prestataires
                                de travaux, échelle régionale pour les prestataires intellectuels).
                            </p>
                            <Button value="Voir l'annuaire des prestataires" variant="secondary" onClick={() => setActiveModal('prestataires')} />
                            <p className="accueil-entreprises-note">
                                Vous souhaitez devenir prestataire ? Consultez nos annonces légales sur les sites
                                BOAMP et JOUE en août et septembre de chaque année pairs. Il convient ensuite de
                                respecter le règlement de consultation.
                            </p>
                        </div>
                        <div className="accueil-entreprises-image">
                            <img src={require('../../assets/about/quisommesnous.webp')} loading="lazy" decoding="async" alt="Équipe de prestataires" />
                        </div>
                    </div>
                </div>

                <div className='accueil-section-block'>
                    <div className="accueil-fonctionnement">

                        <div className="accueil-fonctionnement-row">
                            <h2 className="accueil-fonctionnement-title">
                                Un fonctionnement simple
                            </h2>
                            <div className="accueil-fonctionnement-grid">
                                <div className="accueil-fonctionnement-col">
                                    <div className="accueil-fonctionnement-header">
                                        <span className="accueil-fonctionnement-num">1</span>
                                        <span className="accueil-fonctionnement-label">Sélection</span>
                                    </div>
                                    <div className="accueil-fonctionnement-body">
                                        <p>
                                            Après avoir précisé le lieu de l'opération,
                                        </p>
                                        <p>
                                            Soit je <strong>sélectionne les prestations</strong> qui m'intéressent et les
                                            ajoute à mon panier.
                                        </p>
                                        <p>
                                            Soit je demande à l'entreprise titulaire de rang 1 de me transmettre
                                            un <strong>panier prérempli</strong>, à vérifier avant validation.
                                        </p>
                                        <p>
                                            Si je ne trouve pas ce qui me convient dans le
                                            catalogue, je peux lancer un <strong>marché subséquent</strong>{' '}
                                            et attendre les offres des titulaires.
                                        </p>
                                    </div>
                                </div>
                                <div className="accueil-fonctionnement-col">
                                    <div className="accueil-fonctionnement-header">
                                        <span className="accueil-fonctionnement-num">2</span>
                                        <span className="accueil-fonctionnement-label">Commande</span>
                                    </div>
                                    <div className="accueil-fonctionnement-body">
                                        <p>
                                            L'administrateur de mon compte <strong>valide le panier</strong>.
                                            La centrale <strong>envoie les bons de commande</strong> aux
                                            différentes entreprises concernées qui disposent de 10 jours
                                            pour <strong>accepter</strong> ou refuser <strong>la mission</strong>.
                                        </p>
                                        <p>
                                            En cas de refus et avec votre accord, la commande est
                                            transmise à <strong>l'entreprise de rang suivant</strong>.
                                        </p>
                                    </div>
                                </div>
                                <div className="accueil-fonctionnement-col">
                                    <div className="accueil-fonctionnement-header">
                                        <span className="accueil-fonctionnement-num">3</span>
                                        <span className="accueil-fonctionnement-label">Paiement</span>
                                    </div>
                                    <div className="accueil-fonctionnement-body">
                                        <p>
                                            Les entreprises retenues interviennent dans le
                                            cadre contractuel et me facturent leurs <strong>prestations</strong> directement
                                            via <strong>Chorus Pro</strong>, sans transiter par Centralis.
                                            Je garde un <strong>contrôle total sur le paiement</strong>.
                                        </p>
                                        <p>
                                            Si l'entreprise souhaite utiliser des <strong>sous-traitants</strong>, elle me fait
                                            passer le DC4 pour approbation.
                                        </p>
                                    </div>
                                </div>
                                <div className="accueil-fonctionnement-col">
                                    <div className="accueil-fonctionnement-header">
                                        <span className="accueil-fonctionnement-num">4</span>
                                        <span className="accueil-fonctionnement-label">Délais</span>
                                    </div>
                                    <div className="accueil-fonctionnement-body">
                                        <p>
                                            Chaque entreprise est tenue de respecter le <strong>délai d'exécution
                                                contractuel</strong> – éventuellement modifié par dérogation – faute de
                                            quoi des pénalités peuvent s'appliquer.
                                        </p>
                                    </div>
                                </div>
                            </div>

                        </div>
                        <div className="accueil-fonctionnement-image">
                            <img src={require('../../assets/about/interet.webp')} loading="lazy" decoding="async" alt="Prestataire au travail" />
                        </div>
                    </div>
                </div>

                <div className='accueil-section-block'>
                    <div className="accueil-delai">
                        <div className="accueil-delai-image">
                            <img src={require('../../assets/about/calendrier.webp')} loading="lazy" decoding="async" alt="Calendrier de planification" />
                        </div>
                        <div className="accueil-delai-content">
                            <h2 className="accueil-delai-title">
                                Un délai d'exécution maîtrisé
                            </h2>
                            <p className="accueil-delai-formula">
                                1 journée par tranche de 1.000€ de prestations d'ingénierie commandées<br />
                                1 journée par tranche de 1.500€ de prestations travaux commandées
                            </p>
                            <p className="accueil-delai-example">
                                Exemple pour une rénovation thermique par l'extérieur d'un bâtiment :
                            </p>
                            <p className="accueil-delai-result">
                                Commande de 42.000€ = Réalisation en 28 jours ouvrés
                            </p>
                            <p className="accueil-delai-note">
                                Pour chaque bon de commande, il est possible de déroger à la règle si nécessaire.
                            </p>
                        </div>
                    </div>
                </div>

                <div className='accueil-section-block'>
                    <div className="accueil-gratuit">
                        <div className="accueil-gratuit-image">
                            <img src={require('../../assets/about/montant.webp')} loading="lazy" decoding="async" alt="Calcul des coûts" />
                        </div>
                        <div className="accueil-gratuit-content">
                            <h2 className="accueil-gratuit-title">
                                Gratuit et sans engagement
                            </h2>
                            <p className="accueil-gratuit-price">0,00 €</p>
                            <p>
                                L'utilisation de Centralis est gratuite pour les acheteurs, qui payent{' '}
                                <strong>uniquement le montant des prestations commandées</strong>,
                                et ce directement auprès des entreprises concernées. Depuis 2021, il n'y
                                a plus d'abonnement ou de cotisation à régler à Centralis.
                            </p>
                            <p>
                                Comme intermédiaire, Centralis est rémunéré par les entreprises prestataires, à proportion des
                                commandes qui leur sont confiées.
                            </p>
                            <div className="accueil-gratuit-options">
                                <div className="accueil-gratuit-option">
                                    <div className="accueil-gratuit-option-header">
                                        Pour commander via les<br />prestataires libres
                                    </div>
                                    <div className="accueil-gratuit-option-body">
                                        <p>
                                            Il est possible de s'inscrire à tout moment
                                            (compte libre).
                                        </p>
                                    </div>
                                </div>
                                <div className="accueil-gratuit-option">
                                    <div className="accueil-gratuit-option-header">
                                        Pour commander via les<br />prestataires titulaires
                                    </div>
                                    <div className="accueil-gratuit-option-body">
                                        <p>
                                            Il faut soit, <strong>s'inscrire avant le 31 juillet des
                                                années paires</strong> afin de pouvoir commander à
                                            partir du 1er janvier de l'année impaire qui suit
                                            (le compte libre est alors transformé en
                                            compte formalisé).
                                        </p>
                                        <p>
                                            Soit, être membre, satellite ou filiale d'un
                                            acheteur disposant déjà d'un compte
                                            formalisé. L'accès au compte formalisé est
                                            alors immédiat. Le compte formalisé permet
                                            d'accéder aux deux catalogues, libre et
                                            formalisé.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className='accueil-section-block'>
                    <div className="accueil-presse">
                        <h2 className="accueil-presse-title">
                            On parle de nous
                        </h2>
                        <div className="accueil-presse-list">
                            <div className="accueil-presse-item">
                                <div className="accueil-presse-logo">
                                    <img src={require('../../assets/about/logo-moniteur.webp')} loading="lazy" decoding="async" alt="Le Moniteur" />
                                </div>
                                <div className="accueil-presse-text">
                                    <p>« Le succès de Centralis est au rendez-vous »</p>
                                    <a className="accueil-link" href="https://www.lemoniteur.fr/article/centralis-la-centrale-d-achats-publics-qui-gagne-du-terrain.2098484" target="_blank" rel="noopener noreferrer">Voir l'article</a>
                                </div>
                            </div>
                            <div className="accueil-presse-item">
                                <div className="accueil-presse-logo">
                                    <img src={require('../../assets/about/logo-wekafr-small.webp')} loading="lazy" decoding="async" alt="Weka" />
                                </div>
                                <div className="accueil-presse-text">
                                    <p>« Une solution clé en main pour les acheteurs publics »</p>
                                    <a className="accueil-link" href="https://www.weka.fr/actualite/commande-publique/article/centralis-la-petite-centrale-d-achats-publics-voit-grand-103971/" target="_blank" rel="noopener noreferrer">Voir l'article</a>
                                </div>
                            </div>
                            <div className="accueil-presse-item">
                                <div className="accueil-presse-logo">
                                    <img src={require('../../assets/about/logo-federation-des-epl.webp')} loading="lazy" decoding="async" alt="Fédération des EPL" />
                                </div>
                                <div className="accueil-presse-text">
                                    <p>« Une solution appréciée par plusieurs acheteurs publics dans le secteur de la rénovation de
                                        patrimoine immobilier et de l'aménagement du territoire »</p>
                                    <a className="accueil-link" href="https://www.lesepl.fr/epl-en-action/centralis-la-petite-centrale-dachats-publics-qui-monte-qui-monte/" target="_blank" rel="noopener noreferrer">Voir l'article</a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

            </div> */}

            {/* Footer : fourni par MainLayout (composant Footer partage), identique a l'espace connecte.
                L'ancien footer inline marketing a ete retire pour une source unique de pied de page. */}

            <AccueilModal
                isOpen={activeModal === 'lot-catalogue'}
                onClose={() => setActiveModal(null)}
                title={selectedLot ? `Lot ${selectedLot.code} — ${selectedLot.nom}` : 'Catalogue du lot'}
            >
                <div className="accueil-lot-articles">
                    <div className="modal-articles-search">
                        <SearchBar
                            placeholder="Rechercher une prestation…"
                            value={articlesSearch}
                            onChange={setArticlesSearch}
                            onClear={() => setArticlesSearch('')}
                            loading={false}
                        />
                        {articlesSearch && (
                            <div className="search-info">
                                <span>{filteredArticles.length} résultat{filteredArticles.length !== 1 ? 's' : ''} pour « {articlesSearch} »</span>
                            </div>
                        )}
                    </div>
                    <div className="modal-articles-content">
                        {loadingArticles && (
                            <div className="loading-state">
                                <div className="loading-spinner" />
                                <p>Chargement du catalogue…</p>
                            </div>
                        )}
                        {articlesError && (
                            <div className="error-state"><p>{articlesError}</p></div>
                        )}
                        {!loadingArticles && !articlesError && filteredArticles.length === 0 && (
                            <div className="error-state">
                                <p>{articlesSearch ? `Aucun article trouvé pour « ${articlesSearch} »` : 'Catalogue en cours de constitution pour ce lot.'}</p>
                            </div>
                        )}
                        {!loadingArticles && filteredArticles.length > 0 && (
                            <div className="articles-list">
                                {filteredArticles.map((article) => (
                                    <div
                                        key={article.id}
                                        className="article-container"
                                        onClick={() => setShowArticleLoginPrompt(true)}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={(e) => e.key === 'Enter' && setShowArticleLoginPrompt(true)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <div className="article-row article-row--public">
                                            <div className="article-code">{article.code}</div>
                                            <div className="article-info">
                                                <h4 className="article-nom">{article.nom}</h4>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    {showArticleLoginPrompt && (
                        <div className="accueil-article-prompt-overlay" onClick={() => setShowArticleLoginPrompt(false)}>
                            <div className="accueil-article-prompt-card" onClick={(e) => e.stopPropagation()}>
                                <button
                                    className="accueil-article-prompt-close"
                                    onClick={() => setShowArticleLoginPrompt(false)}
                                    aria-label="Fermer"
                                >&#10005;</button>
                                <h3 className="accueil-article-prompt-title">Accédez au catalogue de prestations</h3>
                                <p className="accueil-article-prompt-text">
                                    Créez votre compte acheteur gratuitement pour consulter les détails des prestations référencées sur Centralis.
                                </p>
                                <div className="accueil-article-prompt-actions">
                                    <Button
                                        value="S'inscrire"
                                        variant="primary"
                                        onClick={() => navigate('/register')}
                                        style={{ fontSize: '1.25rem', padding: '0.85rem 2rem' }}
                                    />
                                    <Button
                                        value="Se connecter"
                                        variant="secondary"
                                        onClick={() => navigate('/login')}
                                        style={{ fontSize: '1.25rem', padding: '0.85rem 2rem' }}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </AccueilModal>

            <AccueilModal
                isOpen={activeModal === 'acheteurs'}
                onClose={() => setActiveModal(null)}
                title="Annuaire acheteur"
            >
                <Annuaire defaultTab="acheteurs" hideTitle hideTabs />
            </AccueilModal>

            <AccueilModal
                isOpen={activeModal === 'prestataires'}
                onClose={() => setActiveModal(null)}
                title="Annuaire des prestataires"
            >
                <Annuaire defaultTab="prestataires" hideTitle hideTabs />
            </AccueilModal>

            <AccueilModal
                isOpen={activeModal === 'catalogue-formalise'}
                onClose={() => setActiveModal(null)}
                title="Catalogue formalisé — Prestataires titulaires"
            >
                <Annuaire defaultTab="prestataires" defaultRang="1" hideTitle hideTabs />
            </AccueilModal>

            <AccueilModal
                isOpen={activeModal === 'catalogue-libre'}
                onClose={() => setActiveModal(null)}
                title="Catalogue libre — Référencement libre"
            >
                <Annuaire defaultTab="prestataires" defaultRang="0" hideTitle hideTabs />
            </AccueilModal>

            <AccueilModal
                isOpen={activeModal === 'publications'}
                onClose={() => setActiveModal(null)}
                title="Publications — Accords-cadres"
            >
                <Publications />
            </AccueilModal>

            <AccueilModal
                isOpen={activeModal === 'video'}
                onClose={() => setActiveModal(null)}
                title="Centralis — Vidéo de présentation"
                className="accueil-modal--video"
            >
                <div className="accueil-video-wrapper">
                    <iframe
                        src="https://www.youtube.com/embed/C8Nnb9myqwg?autoplay=1"
                        title="Centralis — Présentation"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                    />
                </div>
            </AccueilModal>

            <AccueilModal
                isOpen={activeModal === 'page-content'}
                onClose={() => setActiveModal(null)}
                title={footerPageTitle}
                className="accueil-modal--page-content"
            >
                {footerPageNode}
            </AccueilModal>
        </div>
    );
};

export default Accueil;